import React, { useState, useEffect, useContext, useMemo } from "react";
import LayoutWrapper from "../../components/LayoutWrapper";
import HeaderBar from "../../components/HeaderBar";
import {
  ArrowLeft,
  Search,
  FileText,
  Building2,
  Package,
  Eye,
  X,
  Save,
} from "lucide-react";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import axios from "axios";
import { useNavigate, useLocation } from "react-router-dom";
import { ThemeContext } from "../../context/ThemeContext";
import getCurrentSkin from "../../utils/getCurrentSkin";

const BASE_URL = "http://nissi-india.com/api/purReqEntry";

/* ─── Item Details Modal ───────────────────────────────────────────── */
const ItemsModal = ({ items, onClose, baseColor, baseShade, isDark }) => (
  <div 
    className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
    style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
  >
    <div
      className={`relative w-full max-w-4xl max-h-[85vh] flex flex-col rounded-2xl shadow-2xl border overflow-hidden ${
        isDark
          ? "bg-slate-900 border-slate-700"
          : "bg-white border-gray-200"
      }`}
    >
      {/* Header */}
      <div
        className={`flex justify-between items-center px-6 py-4 bg-gradient-to-r from-${baseColor}-600 to-${baseColor}-700 text-white shrink-0`}
      >
        <div className="flex items-center gap-2">
          <Package size={18} />
          <span className="font-bold text-base">Item Details</span>
        </div>
        <button
          onClick={onClose}
          className="p-1.5 rounded-lg bg-white/20 hover:bg-white/30 transition"
        >
          <X size={16} />
        </button>
      </div>

      {/* Body */}
      <div className="overflow-auto flex-1">
        {items.length === 0 ? (
          <div className="text-center text-gray-500 py-16">No items found.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className={`bg-gradient-to-r from-${baseColor}-600 to-${baseColor}-700 text-white sticky top-0`}>
              <tr>
                <th className="px-4 py-3 text-left text-xs font-bold uppercase">S.No</th>
                <th className="px-4 py-3 text-left text-xs font-bold uppercase">Item Code</th>
                <th className="px-4 py-3 text-left text-xs font-bold uppercase">Item Name</th>
                <th className="px-4 py-3 text-left text-xs font-bold uppercase">Part No</th>
                <th className="px-4 py-3 text-left text-xs font-bold uppercase">Specification</th>
                <th className="px-4 py-3 text-right text-xs font-bold uppercase">Qty</th>
              </tr>
            </thead>
            <tbody className={`divide-y ${isDark ? "divide-slate-700" : "divide-gray-200"}`}>
              {items.map((item, idx) => (
                <tr
                  key={idx}
                  className={isDark ? "bg-slate-800/40 hover:bg-slate-700/50" : "bg-white hover:bg-gray-50"}
                >
                  <td className="px-4 py-3 text-center font-medium">{idx + 1}</td>
                  <td className="px-4 py-3 font-mono text-xs">{item.fld_itemcode || "—"}</td>
                  <td className="px-4 py-3 font-medium">{item.fld_itemname || "—"}</td>
                  <td className="px-4 py-3">{item.fld_itemPartNo || "—"}</td>
                  <td className="px-4 py-3">{item.fld_specification || "—"}</td>
                  <td className="px-4 py-3 text-right font-semibold">{item.Qty ?? "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  </div>
);

/* ─── Main Component ───────────────────────────────────────────────── */
const PurchaseReqEnquiryAdd = () => {
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;
  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;

  const navigate = useNavigate();
  const location = useLocation();

  /* ── localStorage / session ───────────────────────────────── */
  const finYearId = localStorage.getItem("finYearId") || "";
  const finYear = localStorage.getItem("finYear") || "";
  const branchIdLS =
    localStorage.getItem("branchId") ||
    localStorage.getItem("branchid") ||
    "8";
  const branch = localStorage.getItem("branchType") || "";
  const storedBranchType =
    localStorage.getItem("branchtype") ||
    (branch === "HO" || branch === "HEAD-OFFICE" ? "HO" : "BRANCH");
  const userId =
    localStorage.getItem("userId") ||
    localStorage.getItem("userid") ||
    "";

  const editIdFromState = location.state?.enquiryid;
  const editIdFromStorage = localStorage.getItem("enquiryid");
  const editId = editIdFromState || editIdFromStorage || "";
  const isEditMode = !!editId;

  const initialBranchType =
    branch === "HO" || branch === "HEAD-OFFICE" ? "HO" : "Branch";

  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": branchIdLS,
      "x-branch": branch,
      "x-userid": userId,
      "x-branchtype": storedBranchType,
    }),
    [finYearId, finYear, branchIdLS, branch, userId, storedBranchType]
  );

  const inputClass = `w-full px-3 py-2.5 rounded-lg border text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-${baseColor}-500/30 focus:border-${baseColor}-500 ${
    isDark
      ? "bg-slate-800/60 border-slate-600/50 text-white placeholder-slate-400"
      : "bg-white border-slate-300 text-slate-900 placeholder-slate-400"
  }`;

  const labelClass = `block text-xs font-semibold tracking-wide mb-1.5 ${
    isDark ? "text-slate-300" : "text-slate-700"
  }`;

  /* ── Form state ──────────────────────────────────────────── */
  const [rbtnType, setBranchTypeRadio] = useState(initialBranchType);
  const [enquiryNo, setEnquiryNo] = useState("");
  const [enquiryDate, setEnquiryDate] = useState(
    new Date().toISOString().split("T")[0]
  );
  const [storeList, setStoreList] = useState([]);
  const [fromStore, setFromStore] = useState("");
  const [supplierList, setSupplierList] = useState([]);
  const [selectedSuppliers, setSelectedSuppliers] = useState([]);

  /* ── Search filter state ─────────────────────────────────── */
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [reqNo, setReqNo] = useState("");
  const [status, setStatus] = useState("");

  /* ── Grid & modal state ──────────────────────────────────── */
  const [data, setData] = useState([]);
  const [selectedRows, setSelectedRows] = useState(new Set());
  const [selectAll, setSelectAll] = useState(false);
  const [modalItems, setModalItems] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  /* ── init ────────────────────────────────────────────────── */
  useEffect(() => {
    if (isEditMode) {
      loadEditDetails(editId);
    } else {
      generateEnquiryNo();
    }

    fetchStores(rbtnType, branchIdLS);
    fetchSuppliers(branchIdLS);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ── Generate Enquiry No ─────────────────────────────────── */
  const generateEnquiryNo = async () => {
    try {
      const res = await axios.get(`${BASE_URL}/generateEnquiryNo`, {
        params: { branchid: branchIdLS },
        headers: commonHeaders,
      });

      if (res.data?.success && res.data.enquiryCode) {
        setEnquiryNo(res.data.enquiryCode.toString());
      } else {
        toast.error("Invalid enquiry number received.");
      }
    } catch (err) {
      console.error("generateEnquiryNo error:", err);
      toast.error("Failed to generate enquiry number.");
    }
  };

  /* ── Fetch Stores ────────────────────────────────────────── */
  const fetchStores = async (bType, bId) => {
    try {
      const res = await axios.get(`${BASE_URL}/getStoresByBranch`, {
        params: {
          branchid: bId,
          branchtype: bType === "HO" ? "HO" : "BRANCH",
        },
        headers: {
          ...commonHeaders,
          "x-branchtype": bType === "HO" ? "HO" : "BRANCH",
        },
      });

      if (Array.isArray(res.data?.stores)) {
        setStoreList(res.data.stores);
        if (res.data.stores.length > 0) {
          setFromStore(res.data.stores[0].fld_storeid);
        } else {
          setFromStore("");
        }
      } else {
        toast.error("Invalid store list received.");
      }
    } catch (err) {
      console.error("fetchStores error:", err);
      toast.error("Failed to fetch stores.");
    }
  };

  /* ── Fetch Suppliers ─────────────────────────────────────── */
  const fetchSuppliers = async (bId) => {
    try {
      const res = await axios.get(`${BASE_URL}/getSuppliersByBranch`, {
        params: { branchid: bId },
        headers: commonHeaders,
      });

      if (Array.isArray(res.data?.suppliers)) {
        setSupplierList(res.data.suppliers);
      } else {
        toast.error("Failed to load suppliers.");
      }
    } catch (err) {
      console.error("fetchSuppliers error:", err);
      toast.error("Failed to fetch suppliers.");
    }
  };

  /* ── Edit Mode ───────────────────────────────────────────── */
  const loadEditDetails = async (eid) => {
    try {
      const res = await axios.get(`${BASE_URL}/getEnquiryEditDetails`, {
        params: { enquiryid: eid },
        headers: commonHeaders,
      });

      const d = res.data || {};

      setEnquiryNo(d.fld_Enquiryno || "");
      setEnquiryDate(
        d.fld_date && d.fld_date.includes("/")
          ? d.fld_date.split("/").reverse().join("-")
          : d.fld_date
          ? d.fld_date.split("T")[0]
          : ""
      );

      setSupplierList(d.suppliers || []);
      setSelectedSuppliers(
        (d.suppliers || []).map((s) => s.fld_supplierid.toString())
      );

      setData(d.requisitions || []);
      setSelectedRows(
        new Set((d.requisitions || []).map((_, i) => i.toString()))
      );
      setSelectAll((d.requisitions || []).length > 0);
    } catch (err) {
      console.error("loadEditDetails error:", err);
      toast.error("Failed to load enquiry details.");
    }
  };

  /* ── Branch type change ──────────────────────────────────── */
  const handleBranchTypeChange = (value) => {
    setBranchTypeRadio(value);
    fetchStores(value === "HO" ? "HO" : "BRANCH", branchIdLS);
  };

  /* ── Date validation ─────────────────────────────────────── */
  const validateDates = () => {
    const today = new Date();
    today.setHours(23, 59, 59, 999);

    if (fromDate && new Date(fromDate) > today) {
      toast.warning("Select from date lesser than current date.");
      return false;
    }
    if (toDate && new Date(toDate) > today) {
      toast.warning("Select to date lesser than or equal to current date.");
      return false;
    }
    if (fromDate && toDate && new Date(toDate) < new Date(fromDate)) {
      toast.warning("Please select to date greater than from date.");
      return false;
    }
    return true;
  };

  const formatDateForAPI = (dateStr) => {
    if (!dateStr) return "";
    const [yyyy, mm, dd] = dateStr.split("-");
    return `${yyyy}-${mm}-${dd}`;
  };

  /* ── Search ──────────────────────────────────────────────── */
  const handleSearch = async () => {
    if (!fromDate || !toDate || !fromStore) {
      toast.warning("Please select From and To dates, From store.");
      return;
    }

    if (!validateDates()) return;

    try {
      const params = {
        branchtype: storedBranchType,
        branchid: branchIdLS,
        rbtnHOChecked: rbtnType === "HO" ? "true" : "false",
        reqno: reqNo,
        fromdate: formatDateForAPI(fromDate),
        todate: formatDateForAPI(toDate),
        fromstore: fromStore,
        status,
      };

      const res = await axios.get(`${BASE_URL}/getPurchaseReqListing`, {
        params,
        headers: {
          ...commonHeaders,
          "x-branchtype": rbtnType === "HO" ? "HO" : "BRANCH",
        },
      });

      if (Array.isArray(res.data?.data)) {
        setData(res.data.data);
        setSelectedRows(new Set());
        setSelectAll(false);

        if (res.data.data.length === 0) {
          toast.info("No records found.");
        }
      } else {
        setData([]);
        toast.info("No records found.");
      }
    } catch (err) {
      console.error("handleSearch error:", err);
      toast.error("Failed to fetch purchase requisition listing.");
    }
  };

  /* ── Select all ──────────────────────────────────────────── */
  const handleSelectAll = (checked) => {
    setSelectAll(checked);
    setSelectedRows(
      checked ? new Set(data.map((_, i) => i.toString())) : new Set()
    );
  };

  /* ── Toggle row ──────────────────────────────────────────── */
  const toggleRow = (idx) => {
    setSelectedRows((prev) => {
      const next = new Set(prev);
      if (next.has(idx.toString())) next.delete(idx.toString());
      else next.add(idx.toString());
      return next;
    });
  };

  /* ── Status text ─────────────────────────────────────────── */
  const getStatusText = (row) => {
    const isCancelled =
      row.fld_isCanceled === "True" || row.fld_isCanceled === true;
    const isClosed = row.fld_isClosed === "True" || row.fld_isClosed === true;
    const isApproved = row.fld_IsApproved === "Y";
    const priorExists =
      row.fld_priorpurchaseexistforall === "True" ||
      row.fld_priorpurchaseexistforall === true;

    if (!isCancelled && !isClosed && priorExists) return "Prior Purchase Exists";
    if (isCancelled && !isClosed) return "Draft";
    if (!isCancelled && isClosed) return "Close";
    if (!isCancelled && !isClosed && isApproved) return "Approved";
    if (isCancelled && isClosed) return "Close";
    return "Waiting";
  };

  /* ── View items ──────────────────────────────────────────── */
  const handleViewItems = async () => {
    if (selectedRows.size === 0) {
      toast.warning("Select any one.");
      return;
    }

    const ids = [...selectedRows]
      .map(
        (i) =>
          data[parseInt(i, 10)]?.fld_PurchaseRequestid ||
          data[parseInt(i, 10)]?.id
      )
      .filter(Boolean)
      .join(",");

    try {
      const res = await axios.get(`${BASE_URL}/getItemsForEnquiry`, {
        params: { prids: ids },
        headers: commonHeaders,
      });

      setModalItems(Array.isArray(res.data?.items) ? res.data.items : []);
      setShowModal(true);
    } catch (err) {
      console.error("handleViewItems error:", err);
      toast.error("Failed to fetch item details.");
    }
  };

  /* ── Submit ──────────────────────────────────────────────── */
/* ── Submit / Update ─────────────────────────────────────────── */
const handleSubmit = async () => {
  if (submitting) return;

  if (!enquiryDate || enquiryDate.trim() === "") {
    toast.error("Please enter value for Date.");
    return;
  }

  if (selectedSuppliers.length === 0) {
    toast.error("Please select any one supplier.");
    return;
  }

  if (selectedRows.size === 0) {
    toast.error("Please select any requisition.");
    return;
  }

  const PRIds = [...selectedRows]
    .map(
      (i) =>
        data[parseInt(i, 10)]?.fld_PurchaseRequestid ||
        data[parseInt(i, 10)]?.id
    )
    .filter(Boolean)
    .join(",");

  const payload = {
    enquiryNo,
    enquiryDate,
    branchId: branchIdLS,
    supplierIDs: selectedSuppliers,
    PRIds,
  };

  // Add enquiryId only in Edit mode
  if (isEditMode) {
    payload.enquiryId = editId;
  }

  setSubmitting(true);

  try {
    const url = isEditMode
      ? `${BASE_URL}/updateEnquiry`
      : `${BASE_URL}/insertEnquiry`;

    const res = await axios.post(url, payload, {
      headers: commonHeaders,
    });

    if (res.data?.success) {
      toast.success(
        isEditMode
          ? "Enquiry updated successfully!"
          : "Enquiry saved successfully!"
      );
      localStorage.removeItem("enquiryid");
      setTimeout(
        () => navigate("/inventory/PurchaseReqEnquiry_Listing"),
        1200
      );
    } else {
      toast.error(res.data?.message || "Failed to save enquiry.");
    }
  } catch (err) {
    console.error("handleSubmit error:", err);
    toast.error(
      err.response?.data?.message || "Please refresh and retry later."
    );
  } finally {
    setSubmitting(false);
  }
};

  return (
    <LayoutWrapper>
      <HeaderBar
        title={
          <span className="font-dmserif text-3xl">
            {isEditMode
              ? "Edit Purchase Requisition Enquiry"
              : "Purchase Requisition Enquiry"}
          </span>
        }
        setSkin={setSkin}
        currentSkin={skin}
      />

      <div className="space-y-6 mt-6">
        {/* Header Card */}
        <div
          className={`p-6 rounded-2xl shadow-xl border flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : `bg-white ${currentSkin.glass} text-black border-gray-200`
          }`}
        >
          <div className="flex items-center gap-3">
            <div
              className={`flex items-center justify-center w-12 h-12 rounded-xl bg-gradient-to-br from-${baseColor}-500 to-${baseColor}-600 shadow-md`}
            >
              <FileText className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold">
                {isEditMode ? "Edit Enquiry" : "New Enquiry"}
              </h1>
              <p
                className={`text-sm ${
                  isDark ? "text-gray-300" : "text-gray-600"
                }`}
              >
                Search requisitions and create supplier enquiry
              </p>
            </div>
            {enquiryNo && (
              <span className="bg-green-100 text-green-800 text-xs font-semibold px-3 py-1 rounded-full">
                {enquiryNo}
              </span>
            )}
          </div>

          <button
            onClick={() => {
              localStorage.removeItem("enquiryid");
              navigate(-1);
            }}
            className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-xl border transition ${
              isDark
                ? "border-slate-600 text-slate-300 hover:bg-slate-800"
                : "border-slate-300 text-slate-700 hover:bg-slate-100"
            }`}
          >
            <ArrowLeft size={16} />
            Back
          </button>
        </div>

        {/* Form Card */}
        <div
          className={`rounded-2xl shadow-xl border overflow-hidden ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10"
              : `bg-white ${currentSkin.glass} border-gray-200`
          }`}
        >
          <div className="p-6 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center gap-3">
              <div
                className={`w-3 h-8 rounded-full bg-${baseColor}-${baseShade}`}
              ></div>
              <Building2 size={20} className={`text-${baseColor}-600`} />
              <h3 className="text-xl font-bold">Enquiry Details</h3>
            </div>
          </div>

          <div className="p-6 space-y-6">
            {/* Branch Type */}
            <div>
              <label className={labelClass}>Branch Type</label>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => handleBranchTypeChange("HO")}
                  disabled={branch !== "HO" && branch !== "HEAD-OFFICE"}
                  className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold transition ${
                    rbtnType === "HO"
                      ? `${buttonClass} ${hoverClass} text-white shadow`
                      : isDark
                      ? "bg-slate-800 text-slate-300 border border-slate-600"
                      : "bg-gray-100 text-gray-700 border border-gray-200"
                  } ${
                    branch !== "HO" && branch !== "HEAD-OFFICE"
                      ? "opacity-50 cursor-not-allowed"
                      : ""
                  }`}
                >
                  <Building2 size={16} />
                  H.O
                </button>

                <button
                  type="button"
                  onClick={() => handleBranchTypeChange("Branch")}
                  disabled={branch === "HO" || branch === "HEAD-OFFICE"}
                  className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold transition ${
                    rbtnType === "Branch"
                      ? "bg-blue-600 text-white shadow"
                      : isDark
                      ? "bg-slate-800 text-slate-300 border border-slate-600"
                      : "bg-gray-100 text-gray-700 border border-gray-200"
                  } ${
                    branch === "HO" || branch === "HEAD-OFFICE"
                      ? "opacity-50 cursor-not-allowed"
                      : ""
                  }`}
                >
                  Branch
                </button>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Left Column */}
              <div className="space-y-5">
                <div>
                  <label className={labelClass}>
                    Enquiry No <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    className={`${inputClass} ${
                      isDark ? "bg-slate-900/60" : "bg-gray-50"
                    }`}
                    value={enquiryNo}
                    readOnly
                  />
                </div>

                <div>
                  <label className={labelClass}>From Date</label>
                  <input
                    type="date"
                    value={fromDate}
                    onChange={(e) => setFromDate(e.target.value)}
                    className={inputClass}
                  />
                </div>

                <div>
                  <label className={labelClass}>
                    Supplier <span className="text-red-500">*</span>
                  </label>
                  <div
                    className={`border rounded-xl h-32 overflow-y-auto px-3 py-2 space-y-1.5 ${
                      isDark
                        ? "bg-slate-800/60 border-slate-600"
                        : "bg-white border-slate-300"
                    }`}
                  >
                    {supplierList.length === 0 ? (
                      <div className="text-gray-400 text-sm py-2">
                        Loading suppliers...
                      </div>
                    ) : (
                      supplierList.map((supplier, idx) => (
                        <label
                          key={idx}
                          className="flex gap-2 items-center cursor-pointer text-sm"
                        >
                          <input
                            type="checkbox"
                            className="w-4 h-4 rounded"
                            checked={selectedSuppliers.includes(
                              supplier.fld_supplierid.toString()
                            )}
                            onChange={(e) => {
                              const id = supplier.fld_supplierid.toString();
                              setSelectedSuppliers((prev) =>
                                e.target.checked
                                  ? [...prev, id]
                                  : prev.filter((x) => x !== id)
                              );
                            }}
                          />
                          {supplier.fld_suppliername}
                        </label>
                      ))
                    )}
                  </div>
                </div>

                <div>
                  <label className={labelClass}>Store</label>
                  <select
                    value={fromStore}
                    onChange={(e) => setFromStore(e.target.value)}
                    className={inputClass}
                  >
                    <option value="">Select</option>
                    {storeList.map((store) => (
                      <option key={store.fld_storeid} value={store.fld_storeid}>
                        {store.fld_storename}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Right Column */}
              <div className="space-y-5">
                <div>
                  <label className={labelClass}>
                    Enquiry Date <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    value={enquiryDate}
                    onChange={(e) => setEnquiryDate(e.target.value)}
                    className={inputClass}
                  />
                </div>

                <div>
                  <label className={labelClass}>To Date</label>
                  <input
                    type="date"
                    value={toDate}
                    onChange={(e) => setToDate(e.target.value)}
                    className={inputClass}
                  />
                </div>

                <div>
                  <label className={labelClass}>Req No</label>
                  <input
                    type="text"
                    value={reqNo}
                    onChange={(e) => setReqNo(e.target.value)}
                    className={inputClass}
                    placeholder="Request number"
                  />
                </div>

                <div>
                  <label className={labelClass}>Status</label>
                  <select
                    value={status}
                    onChange={(e) => setStatus(e.target.value)}
                    className={inputClass}
                  >
                    <option value="">Select</option>
                    <option value="Approved">Approved</option>
                    <option value="Waiting">Waiting</option>
                    <option value="Draft">Draft</option>
                    <option value="Close">Close</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Search Button */}
            <div className="flex justify-end pt-2">
              <button
                onClick={handleSearch}
                className={`inline-flex items-center gap-2 px-6 py-2.5 text-white text-sm font-semibold rounded-xl shadow transition ${buttonClass} ${hoverClass}`}
              >
                <Search size={16} />
                Search Requisitions
              </button>
            </div>
          </div>
        </div>

        {/* Grid Card */}
        <div
          className={`rounded-2xl shadow-xl border overflow-hidden ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10"
              : `bg-white ${currentSkin.glass} border-gray-200`
          }`}
        >
          <div className="p-6 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <div
                  className={`w-3 h-8 rounded-full bg-${baseColor}-${baseShade}`}
                ></div>
                <h3 className="text-xl font-bold">Purchase Requisitions</h3>
                {data.length > 0 && (
                  <span
                    className={`px-2.5 py-1 rounded-full text-xs font-semibold ${
                      isDark
                        ? "bg-gray-700 text-gray-300"
                        : "bg-gray-100 text-gray-600"
                    }`}
                  >
                    {data.length} records · {selectedRows.size} selected
                  </span>
                )}
              </div>

              {data.length > 0 && (
                <button
                  onClick={handleViewItems}
                  className={`inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold rounded-xl border transition ${
                    isDark
                      ? "border-slate-600 text-slate-300 hover:bg-slate-800"
                      : "border-slate-300 text-slate-700 hover:bg-slate-100"
                  }`}
                >
                  <Eye size={16} />
                  View Selected Items
                </button>
              )}
            </div>
          </div>

          {data.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16">
              <FileText size={48} className="text-gray-400 mb-4" />
              <h3 className="text-lg font-semibold text-gray-700 dark:text-gray-300 mb-2">
                No Records Found
              </h3>
              <p className="text-gray-500 text-center max-w-md text-sm">
                Use the filters above and click Search Requisitions.
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead
                  className={`bg-gradient-to-r from-${baseColor}-600 to-${baseColor}-700 text-white`}
                >
                  <tr>
                    <th className="px-4 py-3 text-center text-xs font-bold uppercase">
                      <label className="flex items-center justify-center gap-1.5 cursor-pointer">
                        <input
                          type="checkbox"
                          className="w-4 h-4 rounded"
                          checked={selectAll}
                          onChange={(e) => handleSelectAll(e.target.checked)}
                        />
                        Select
                      </label>
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-bold uppercase">
                      S.No
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-bold uppercase">
                      Request No
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-bold uppercase">
                      Date
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-bold uppercase">
                      Type
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-bold uppercase">
                      Indent Ref
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-bold uppercase">
                      From Store
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-bold uppercase">
                      HO Store
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-bold uppercase">
                      Status
                    </th>
                  </tr>
                </thead>

                <tbody
                  className={`divide-y ${
                    isDark ? "divide-gray-600" : "divide-gray-200"
                  }`}
                >
                  {data.map((item, idx) => {
                    const statusText = getStatusText(item);
                    const isChecked = selectedRows.has(idx.toString());

                    return (
                      <tr
                        key={idx}
                        className={`cursor-pointer transition ${
                          isChecked
                            ? isDark
                              ? "bg-blue-900/30"
                              : "bg-blue-50"
                            : isDark
                            ? "bg-gray-800/20 hover:bg-gray-700/30"
                            : "bg-white hover:bg-gray-50"
                        }`}
                        onClick={() => toggleRow(idx)}
                      >
                        <td
                          className="px-4 py-3 text-center"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <input
                            type="checkbox"
                            className="w-4 h-4 rounded"
                            checked={isChecked}
                            onChange={() => toggleRow(idx)}
                          />
                        </td>
                        <td className="px-4 py-3 font-medium">{idx + 1}</td>
                        <td className="px-4 py-3">
                          <span
                            className={`inline-block px-2.5 py-1 rounded-full text-xs font-semibold ${
                              isDark
                                ? "bg-gray-700 text-gray-200"
                                : `bg-${baseColor}-100 text-${baseColor}-800`
                            }`}
                          >
                            {item.fld_purchaseRequestNo}
                          </span>
                        </td>
                        <td className="px-4 py-3">{item.fld_date}</td>
                        <td className="px-4 py-3">{item.fld_type}</td>
                        <td className="px-4 py-3">{item.fld_indentref || "—"}</td>
                        <td className="px-4 py-3">{item.fromstore || "—"}</td>
                        <td className="px-4 py-3">{item.ToStore || "—"}</td>
                        <td className="px-4 py-3">
                          <span
                            className={`inline-flex px-2.5 py-1 text-xs font-semibold rounded-full ${
                              statusText === "Approved"
                                ? "bg-green-100 text-green-800"
                                : statusText === "Waiting"
                                ? "bg-yellow-100 text-yellow-800"
                                : statusText === "Draft"
                                ? "bg-indigo-100 text-indigo-800"
                                : statusText === "Close"
                                ? "bg-gray-100 text-gray-700"
                                : statusText === "Prior Purchase Exists"
                                ? "bg-red-100 text-red-700"
                                : "bg-gray-100 text-gray-700"
                            }`}
                          >
                            {statusText}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Submit */}
        <div className="flex justify-end pb-8">
          <button
            onClick={handleSubmit}
            disabled={submitting}
            className={`inline-flex items-center gap-2 px-8 py-3 text-white text-sm font-bold rounded-xl shadow-lg transition ${buttonClass} ${hoverClass} disabled:opacity-50`}
          >
            {submitting ? (
              <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
            ) : (
              <Save size={18} />
            )}
            {submitting
              ? "Saving..."
              : isEditMode
              ? "Update Enquiry"
              : "Submit Enquiry"}
          </button>
        </div>
      </div>

      {/* Modal */}
      {showModal && (
        <ItemsModal
          items={modalItems}
          onClose={() => setShowModal(false)}
          baseColor={baseColor}
          baseShade={baseShade}
          isDark={isDark}
        />
      )}

      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        closeOnClick
        pauseOnHover
        theme={isDark ? "dark" : "light"}
      />
    </LayoutWrapper>
  );
};

export default PurchaseReqEnquiryAdd;